import * as React from "react"
import { NavLink, useLocation } from "react-router-dom"
import { ChevronRight } from "lucide-react"

import { useAuth } from "@/contexts/AuthContext"
import { menuItems } from "@/components/Layout/navigation"
import { NavUser } from "@/components/nav-user"
import logoIcon from "@/assets/images/logoicon.svg"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "@/components/ui/sidebar"

/**
 * The dashboard's navigation, rendered with shadcn's sidebar-07 shell.
 *
 * Items and permissions come from Layout/navigation.js — the same source the
 * original Layout/Sidebar.js uses — so the two cannot drift apart.
 *
 * Differences from the stock block, all driven by this app's needs:
 *  - permission gating, so an operator only sees what their role allows;
 *  - react-router NavLinks instead of plain <a>, so navigation stays client-side;
 *  - the team switcher replaced by the product's own branding (there are no
 *    teams to switch between here).
 */
export function AppSidebar({ ...props }) {
  const { hasPermission } = useAuth()
  const location = useLocation()

  // Matches Layout/Sidebar.js exactly: a nav entry is active for its own path
  // and anything beneath it, so /drivers stays lit while on /drivers/create.
  const isActive = React.useCallback(
    (path) => location.pathname === path || location.pathname.startsWith(path + "/"),
    [location.pathname],
  )

  // Hide anything this role cannot open, and drop a parent whose children are
  // all hidden — a group that expands to nothing is worse than no group.
  const visibleItems = React.useMemo(() => {
    return menuItems
      .filter((item) => hasPermission(item.permission))
      .map((item) => {
        const subItems = (item.subItems || []).filter((sub) =>
          hasPermission(sub.permission),
        )
        return { ...item, subItems }
      })
  }, [hasPermission])

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <NavLink to="/dashboard">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <img src={logoIcon} alt="" className="size-5" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Public Rides</span>
                  <span className="truncate text-xs">Admin Dashboard</span>
                </div>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {visibleItems.map((item) => {
              const Icon = item.icon
              const active = isActive(item.path)

              // Leaf item — navigate straight there.
              if (!item.subItems.length) {
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild tooltip={item.title} isActive={active}>
                      <NavLink to={item.path}>
                        {Icon && <Icon />}
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              }

              // Parent with children — open by default when the current route
              // is inside it, so a refresh lands with the right group expanded.
              return (
                <Collapsible
                  key={item.title}
                  asChild
                  defaultOpen={active}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip={item.title} isActive={active}>
                        {Icon && <Icon />}
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.subItems.map((sub) => (
                          <SidebarMenuSubItem key={sub.title}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={location.pathname === sub.path}
                            >
                              <NavLink to={sub.path}>
                                <span>{sub.title}</span>
                              </NavLink>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              )
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
